/**
 * Select Component
 * Custom dropdown select with label and error handling
 * Supports optional colored indicators via color prop on options
 */

import type { SelectHTMLAttributes } from 'react';
import { forwardRef, useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '../../lib/utils';

export interface SelectOption {
  value: string;
  label: string;
  color?: string; // Tailwind text color class for bullet (e.g., 'text-green-500')
}

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string;
  helperText?: string;
  options: SelectOption[];
  placeholder?: string;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ className, label, error, helperText, options, placeholder, id, value, onChange, ...props }, _ref) => {
    const selectId = id || label?.toLowerCase().replace(/\s+/g, '-');

    // Custom dropdown state
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Close dropdown when clicking outside
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
          setIsOpen(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Get selected option
    const selectedOption = options.find(opt => opt.value === value);

    // Handle option selection
    const handleOptionClick = (optionValue: string) => {
      if (onChange) {
        const syntheticEvent = {
          target: { name: props.name || '', value: optionValue }
        } as React.ChangeEvent<HTMLSelectElement>;
        onChange(syntheticEvent);
      }
      setIsOpen(false);
    };

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={selectId}
            className="block text-sm font-medium text-body mb-1"
          >
            {label}
            {props.required && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}
        <div className="relative" ref={dropdownRef}>
          {/* Dropdown trigger button */}
          <button
            type="button"
            id={selectId}
            onClick={() => !props.disabled && setIsOpen(!isOpen)}
            className={cn(
              'block w-full rounded-md border-strong shadow-sm focus:border-focus focus:ring-blue-500 text-sm text-heading',
              'px-3 py-2 pr-10 border text-left bg-surface',
              error && 'border-red-300 dark:border-red-700 focus:border-red-500 focus:ring-red-500',
              props.disabled && 'bg-surface-secondary cursor-not-allowed',
              className
            )}
          >
            {selectedOption ? (
              <span className="flex items-center">
                {selectedOption.color && (
                  <span className={`${selectedOption.color} mr-2`}>●</span>
                )}
                <span className="text-heading">{selectedOption.label}</span>
              </span>
            ) : (
              <span className="text-meta">{placeholder || 'Select...'}</span>
            )}
          </button>
          <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
            <ChevronDown className={cn('h-4 w-4 text-meta transition-transform', isOpen && 'rotate-180')} />
          </div>

          {/* Dropdown panel */}
          {isOpen && (
            <div className="absolute z-50 mt-1 w-full bg-surface border border-strong rounded-md shadow-lg max-h-[280px] overflow-y-auto">
              {options.map((option) => (
                <div
                  key={option.value}
                  onClick={() => handleOptionClick(option.value)}
                  className={cn(
                    'px-3 py-2 cursor-pointer flex items-center hover:bg-surface-hover',
                    option.value === value && 'bg-blue-50 dark:bg-blue-900/20'
                  )}
                >
                  {option.color && (
                    <span className={`${option.color} mr-2`}>●</span>
                  )}
                  <span className="text-heading">{option.label}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        {error && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{error}</p>}
        {helperText && !error && (
          <p className="mt-1 text-sm text-meta">{helperText}</p>
        )}
      </div>
    );
  }
);

Select.displayName = 'Select';
